import http from '@/api/http'
import { TReportJobStatus, TRequestReportResponse } from '@/types/report'

import {
  ProcessingsPayload,
  ProcessingsResponse,
  ProcessingsMetricsResponse,
  ProcessingPayload,
  ProcessingResponse,
  CreateProcessingPayload,
  CreateProcessingResponse,
  CheckUploadReadyResponse,
  ProcessingCollectionsPayload,
  ProcessingCollectionsResponse,
  ProcessingCollectionsMetricsPayload,
  ProcessingCollectionsMetricsResponse,
  ProcessingCollectionsFiltersPayload,
  ProcessingCollectionsFiltersResponse,
  ExportProcessingCollectionsPayload,
  ExportProcessingCollectionsResponse,
  ProcessingSchoolsPayload,
  ProcessingSchoolsResponse,
  ProcessingSchoolsMetricsPayload,
  ProcessingSchoolsMetricsResponse,
  ProcessingSchoolsFiltersPayload,
  ProcessingSchoolsFiltersResponse,
  ExportProcessingSchoolsPayload,
  ExportProcessingSchoolsResponse,
  RequestProcessingSchoolsReportPayload,
  GetProcessingSchoolsReportStatusPayload,
  ExportTechnicalAssignmentPayload,
  ExportTechnicalAssignmentResponse,
  RequestCalculationMemoryReportPayload,
  ValidateSelectionPayload,
  ValidateSelectionResponse,
  ValidateServiceMapPayload,
  ValidateServiceMapResponse,
} from './types'

export const processings = async (payload: ProcessingsPayload) => {
  const response = await http.get<ProcessingsResponse>('/processing/', {
    params: payload,
  })

  return response.data
}

export const processingsMetrics = async () => {
  const response = await http.get<ProcessingsMetricsResponse>(
    '/processing/metrics/'
  )

  return response.data
}

export const processing = async (payload: ProcessingPayload) => {
  const { id } = payload

  const response = await http.get<ProcessingResponse>(`/processing/${id}/`)

  return response.data
}

export const createProcessing = async (payload: CreateProcessingPayload) => {
  const {
    nuDeedType,
    nuEducationStage,
    adherenceUploadId,
    selectionUploadId,
    serviceMapUploadId,
  } = payload

  const response = await http.post<CreateProcessingResponse>('/processing/', {
    nuDeedType,
    nuEducationStage,
    adherenceUploadId,
    selectionUploadId,
    // PSV-424: só com mapa. Sem ele o corpo fica idêntico ao de antes.
    ...(serviceMapUploadId ? { serviceMapUploadId } : {}),
  })

  return response.data
}

/**
 * PSV-387 — o timeout de 150s da instancia `http` nao serve aqui.
 *
 * Este endpoint le o relatorio de escolha de forma sincrona no worker web, e o
 * arquivo passou a poder ter 800 MB (o da cliente tem 560 MB, num `media`
 * montado por NFS). O upload em si nao corre esse risco, porque o TUS usa XHR
 * cru; a conferencia que roda logo depois, sim -- e um timeout aqui cai no
 * ramo de erro que ainda libera o envio, ou seja, processa sem a conferencia
 * ter rodado.
 *
 * 300s alinha com quem de fato decide: `--timeout=300` do gunicorn e o
 * `proxy-read-timeout: "300"` do ingress.
 */
const VALIDATE_SELECTION_TIMEOUT_MS = 300000

export const validateSelectionReport = async (
  payload: ValidateSelectionPayload
): Promise<ValidateSelectionResponse> => {
  const response = await http.post<ValidateSelectionResponse>(
    '/processing/validate-selection/',
    payload,
    { timeout: VALIDATE_SELECTION_TIMEOUT_MS }
  )

  return response.data
}

export const validateServiceMap = async (
  payload: ValidateServiceMapPayload
): Promise<ValidateServiceMapResponse> => {
  const response = await http.post<ValidateServiceMapResponse>(
    '/processing/validate-service-map/',
    payload
  )

  return response.data
}

export const checkUploadReady = async (
  uploadId: string
): Promise<CheckUploadReadyResponse | null> => {
  try {
    const response = await http.get<CheckUploadReadyResponse>(
      `/processing/uploads/${uploadId}/`
    )
    return response.data
  } catch {
    return null
  }
}

export const processingCollections = async (
  payload: ProcessingCollectionsPayload
) => {
  const { processingId, ...params } = payload

  const response = await http.get<ProcessingCollectionsResponse>(
    `/processing/${processingId}/collections/`,
    {
      params,
    }
  )

  return response.data
}

export const processingCollectionsMetrics = async (
  payload: ProcessingCollectionsMetricsPayload
) => {
  const { processingId, ...params } = payload

  const response = await http.get<ProcessingCollectionsMetricsResponse>(
    `/processing/${processingId}/collections/metrics/`,
    {
      params,
    }
  )

  return response.data
}

export const processingCollectionsFilters = async (
  payload: ProcessingCollectionsFiltersPayload
) => {
  const { processingId } = payload

  const response = await http.get<ProcessingCollectionsFiltersResponse>(
    `/processing/${processingId}/collections/filters/`
  )

  return response.data
}

export const exportProcessingCollections = async (
  payload: ExportProcessingCollectionsPayload
) => {
  const { processingId, ...params } = payload

  const response = await http.get<ExportProcessingCollectionsResponse>(
    `/processing/${processingId}/collections/export/`,
    {
      params,
      responseType: 'blob',
    }
  )

  return response.data
}

export const processingSchools = async (payload: ProcessingSchoolsPayload) => {
  const { processingId, ...params } = payload

  const response = await http.get<ProcessingSchoolsResponse>(
    `/processing/${processingId}/schools/`,
    {
      params,
    }
  )

  return response.data
}

export const processingSchoolsMetrics = async (
  payload: ProcessingSchoolsMetricsPayload
) => {
  const { processingId, ...params } = payload

  const response = await http.get<ProcessingSchoolsMetricsResponse>(
    `/processing/${processingId}/schools/metrics/`,
    {
      params,
    }
  )

  return response.data
}

export const processingSchoolsFilters = async (
  payload: ProcessingSchoolsFiltersPayload
) => {
  const { processingId, ...params } = payload

  const response = await http.get<ProcessingSchoolsFiltersResponse>(
    `/processing/${processingId}/schools/filters/`,
    {
      params,
    }
  )

  return response.data
}

export const exportProcessingSchools = async (
  payload: ExportProcessingSchoolsPayload
) => {
  const { processingId, ...params } = payload

  const response = await http.get<ExportProcessingSchoolsResponse>(
    `/processing/${processingId}/schools/export/`,
    {
      params,
      responseType: 'blob',
    }
  )

  return response.data
}

export const requestProcessingSchoolsReport = async (
  payload: RequestProcessingSchoolsReportPayload
): Promise<TRequestReportResponse> => {
  const { processingId, ...params } = payload

  const response = await http.post<TRequestReportResponse>(
    `/processing/${processingId}/schools/request-report/`,
    null,
    { params }
  )

  return response.data
}

export const getProcessingSchoolsReportStatus = async (
  payload: GetProcessingSchoolsReportStatusPayload
): Promise<TReportJobStatus> => {
  const { processingId, jobId } = payload

  const response = await http.get<TReportJobStatus>(
    `/processing/${processingId}/schools/report-status/${jobId}/`
  )

  return response.data
}

export const cancelAllProcessings = async (): Promise<{
  cancelled: number
}> => {
  const response = await http.post<{ cancelled: number }>(
    '/processing/cancel-all/'
  )

  return response.data
}

export const exportTechnicalAssignment = async (
  payload: ExportTechnicalAssignmentPayload
) => {
  const { processingId } = payload

  const response = await http.get<ExportTechnicalAssignmentResponse>(
    `/processing/${processingId}/schools/technical-assignment/export/`,
    { responseType: 'blob' }
  )

  return response.data
}

export const requestCalculationMemoryReport = async (
  payload: RequestCalculationMemoryReportPayload
): Promise<TRequestReportResponse> => {
  const { processingId, formato } = payload
  const url = `/processing/${processingId}/schools/calculation-memory/request-report/`

  // Sem formato, a chamada fica idêntica à de antes da PSV-460 (xlsx).
  const response = formato
    ? await http.post<TRequestReportResponse>(url, undefined, {
        params: { formato },
      })
    : await http.post<TRequestReportResponse>(url)

  return response.data
}

export const downloadTemplate = async (
  type: 'adesao' | 'escolha' | 'mapa_atendimento'
): Promise<Blob> => {
  const response = await http.get<Blob>(`/processing/templates/${type}/`, {
    responseType: 'blob',
  })

  return response.data
}
